/**
 * Expands ADS abbreviations within free-text notes.
 *
 * Matching rules:
 * - Whole-word, case-sensitive: only the exact (uppercase) abbreviation as
 *   written is expanded, so ordinary lowercase prose is never altered. Slashes
 *   are treated as part of the token so entries like `A/C` and `L/E` match as a
 *   unit.
 * - Longest-first: `BITE` is preferred over `BIT`, `WXR` over `WX`, etc.
 * - The single-letter `L`/`R` abbreviations are applied in a second pass so they
 *   only ever match standalone tokens and never clobber multi-character
 *   abbreviations.
 *
 * The abbreviation set comes from the runtime abbreviationStore (seeded from the
 * static list, refreshed from the /abbreviations API). Compiled patterns and
 * lookups are cached and rebuilt only when the store version changes.
 */
import { AbbreviationEntry } from '../data/adsAbbreviations';
import { getMainEntries, getStandaloneEntries, getStoreVersion } from '../data/abbreviationStore';

/** Characters that count as part of an abbreviation token (used for boundaries). */
const TOKEN_CHARS = 'A-Za-z0-9/';

/** Escapes a string for safe use inside a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Builds a global RegExp that matches any of the given abbreviations as a whole
 * token, or `null` when there are no entries (so callers skip the pass rather
 * than run an empty alternation that would match everywhere). Group 1 captures
 * the (non-token) character preceding the match so it can be preserved in the
 * replacement; a lookahead guards the trailing side.
 */
function buildPattern(entries: readonly AbbreviationEntry[]): RegExp | null {
  if (entries.length === 0) return null;
  const alternation = [...entries]
    .sort((a, b) => b.abbreviation.length - a.abbreviation.length)
    .map((entry) => escapeRegExp(entry.abbreviation))
    .join('|');
  return new RegExp(`(^|[^${TOKEN_CHARS}])(${alternation})(?![${TOKEN_CHARS}])`, 'g');
}

function buildLookup(entries: readonly AbbreviationEntry[]): Map<string, string> {
  return new Map(entries.map((entry) => [entry.abbreviation, entry.expansion]));
}

/** Compiled matcher/lookup pair for both abbreviation passes. */
interface CompiledExpander {
  mainPattern: RegExp | null;
  mainLookup: Map<string, string>;
  standalonePattern: RegExp | null;
  standaloneLookup: Map<string, string>;
}

let cachedVersion = -1;
let compiled: CompiledExpander | null = null;

/** Returns the compiled patterns/lookups, rebuilding them when the store changes. */
function getCompiled(): CompiledExpander {
  const currentVersion = getStoreVersion();
  if (!compiled || currentVersion !== cachedVersion) {
    const mainEntries = getMainEntries();
    const standaloneEntries = getStandaloneEntries();
    compiled = {
      mainPattern: buildPattern(mainEntries),
      mainLookup: buildLookup(mainEntries),
      standalonePattern: buildPattern(standaloneEntries),
      standaloneLookup: buildLookup(standaloneEntries),
    };
    cachedVersion = currentVersion;
  }
  return compiled;
}

function replaceWith(lookup: Map<string, string>) {
  return (_match: string, lead: string, abbreviation: string): string =>
    `${lead}${lookup.get(abbreviation) ?? abbreviation}`;
}

/**
 * Returns the note text with all ADS abbreviations expanded. Idempotent: text
 * that contains no (uppercase) abbreviations — including already-expanded text —
 * is returned unchanged.
 */
export function expandAbbreviations(text: string): string {
  if (!text) return text;
  const { mainPattern, mainLookup, standalonePattern, standaloneLookup } = getCompiled();
  let result = text;
  if (mainPattern) result = result.replace(mainPattern, replaceWith(mainLookup));
  if (standalonePattern) result = result.replace(standalonePattern, replaceWith(standaloneLookup));
  return result;
}
